import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Student {
  id: string;
  name: string;
  email: string | null;
  studio_id: string;
}

interface Membership {
  id: string;
  name: string;
  price: string;
}

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  memberships: Membership[];
  getAuthToken: () => string | null;
  onSuccess?: (message: string) => void;
}

export default function RecordPaymentModal({
  isOpen,
  onClose,
  student,
  memberships,
  getAuthToken,
  onSuccess,
}: RecordPaymentModalProps) {
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    period_start: new Date().toISOString().split('T')[0],
    period_end: '',
    payment_method: 'cash',
    membership_ids: [] as string[],
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate period_end based on period_start (default to 1 month)
  useEffect(() => {
    if (formData.period_start && !formData.period_end) {
      const startDate = new Date(formData.period_start);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1);
      setFormData(prev => ({
        ...prev,
        period_end: endDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.period_start, formData.period_end]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!student) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Get selected membership names
      const selectedMembershipNames = formData.membership_ids.length > 0
        ? formData.membership_ids
            .map(id => memberships.find(m => m.id === id)?.name)
            .filter(Boolean)
            .join(', ')
        : '';

      // Create a single payment record with the total amount
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          student_id: student.id,
          studio_id: student.studio_id,
          amount: parseFloat(formData.amount),
          payment_date: formData.payment_date,
          period_start: formData.period_start,
          period_end: formData.period_end,
          payment_method: formData.payment_method,
          membership_id: formData.membership_ids.length > 0 ? formData.membership_ids[0] : null, // Link to first membership if any
          membership_names: selectedMembershipNames || null, // Send all membership names
          notes: formData.notes || null, // Keep notes separate
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to record payment');
      }

      // Get payment data
      const paymentData = await response.json();
      const paymentIds = [paymentData.id];

      // Send email receipt if student has an email
      let emailSent = false;
      if (student.email && paymentIds.length > 0) {
        try {
          const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manual-payments/send-receipt`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getAuthToken()}`,
            },
            body: JSON.stringify({
              payment_ids: paymentIds,
              student_id: student.id,
              student_email: student.email,
            }),
          });
          emailSent = emailResponse.ok;
        } catch (emailError) {
          console.error('Failed to send receipt email:', emailError);
          // Don't fail the whole operation if email fails
        }
      }

      // Show success notification
      const successMessage = student.email && emailSent
        ? `Payment of $${formData.amount} recorded successfully and email receipt sent to ${student.email}!`
        : student.email
        ? `Payment of $${formData.amount} recorded successfully! (Email receipt failed to send)`
        : `Payment of $${formData.amount} recorded successfully! (No email on file)`;
      
      if (onSuccess) {
        onSuccess(successMessage);
      }

      // Reset form and close modal
      setFormData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        period_start: new Date().toISOString().split('T')[0],
        period_end: '',
        payment_method: 'cash',
        membership_ids: [],
        notes: '',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !student) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Record Cash Payment
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Student
            </label>
            <input
              type="text"
              value={student.name}
              disabled
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Amount *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              required
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Date *
            </label>
            <input
              type="date"
              required
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Period Start *
              </label>
              <input
                type="date"
                required
                value={formData.period_start}
                onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Period End *
              </label>
              <input
                type="date"
                required
                value={formData.period_end}
                onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Method *
            </label>
            <select
              required
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="apple_pay">Apple Pay</option>
              <option value="google_pay">Google Pay</option>
              <option value="venmo">Venmo</option>
              <option value="paypal">PayPal</option>
              <option value="cashapp">Cash App</option>
              <option value="zelle">Zelle</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Membership Types (Optional)
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-gray-50 dark:bg-gray-700">
              {memberships.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No memberships available</p>
              ) : (
                memberships.map((membership) => (
                  <label
                    key={membership.id}
                    className="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.membership_ids.includes(membership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            membership_ids: [...formData.membership_ids, membership.id],
                          });
                        } else {
                          setFormData({
                            ...formData,
                            membership_ids: formData.membership_ids.filter(id => id !== membership.id),
                          });
                        }
                      }}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                      {membership.name}
                    </span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      ${membership.price}
                    </span>
                  </label>
                ))
              )}
            </div>
            {formData.membership_ids.length > 0 && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formData.membership_ids.length} membership{formData.membership_ids.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Add any notes about this payment..."
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex-shrink-0">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Recording...' : 'Record Payment & Send Receipt'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

